import React, { useState, useRef, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowRight, ArrowLeft, ShieldCheck, Fingerprint, Loader2, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCustomerAuth, API } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import { checkPasskey, passkeySupported } from '../lib/passkey';

const CustomerLogin = () => {
  const { token, requestOtp, verifyOtp, requestSmsOtp, verifySmsOtp, signInWithPasskey } = useCustomerAuth();
  // method: 'email' (default 4-digit email OTP) | 'sms' (4-digit SMS OTP via D7).
  // The SMS tab is only shown when admin has enabled it server-side.
  const [method, setMethod] = useState('email');
  const [step, setStep] = useState('email'); // email | code  (when method=sms: phone | code)
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [code, setCode] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [pkSupported, setPkSupported] = useState(false);
  const [emailHasPasskey, setEmailHasPasskey] = useState(false);
  const [autoVerifyTried, setAutoVerifyTried] = useState(false);
  const inputsRef = useRef([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  useEffect(() => {
    setPkSupported(passkeySupported());
  }, []);

  // Discover whether SMS OTP login is currently enabled for customers. The
  // tab is hidden entirely when off so the storefront doesn't tease an
  // option that would just 403.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/sms-otp-config`);
        setSmsEnabled(!!data?.customer_enabled);
      } catch {
        setSmsEnabled(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!email || !email.includes('@')) {
      setEmailHasPasskey(false);
      return;
    }
    const t = setTimeout(() => {
      checkPasskey(email).then(setEmailHasPasskey);
    }, 400);
    return () => clearTimeout(t);
  }, [email]);

  if (token) return <Navigate to="/my-orders" replace />;

  const showPasskeyButton = pkSupported && method === 'email';

  const verifyCode = async (otpCode) => {
    const full = otpCode || code.join('');
    if (full.length !== 4) {
      toast({ title: 'Enter the 4-digit code' });
      return;
    }
    setLoading(true);
    const res = method === 'sms' ? await verifySmsOtp(email, full) : await verifyOtp(email, full);
    setLoading(false);
    if (res.ok) {
      toast({ title: 'Welcome!', description: 'You\u2019re signed in.' });
      navigate('/my-orders', { replace: true });
    } else {
      toast({ title: 'Invalid code', description: res.error });
      setCode(['', '', '', '']);
      setAutoVerifyTried(false);
      inputsRef.current[0]?.focus();
    }
  };

  const handleBiometricLogin = async () => {
    if (!email || !email.includes('@')) {
      toast({ title: 'Enter your email first', description: 'We need it to find your passkey.' });
      return;
    }
    setPasskeyLoading(true);
    const has = emailHasPasskey || (await checkPasskey(email));
    if (!has) {
      setPasskeyLoading(false);
      toast({
        title: 'No passkey on this account',
        description: 'Sign in with the email code once, then enable Face ID / Touch ID from your account.',
      });
      return;
    }
    const res = await signInWithPasskey(email);
    setPasskeyLoading(false);
    if (res.ok) {
      toast({ title: 'Signed in', description: 'Welcome back!' });
      navigate('/my-orders', { replace: true });
    } else {
      toast({ title: 'Biometric sign-in failed', description: res.error });
    }
  };

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    if (method === 'sms' && !phone.trim()) {
      toast({ title: 'Enter your phone number' });
      return;
    }
    setLoading(true);
    const res = method === 'sms'
      ? await requestSmsOtp(email, phone.trim())
      : await requestOtp(email);
    setLoading(false);
    if (res.ok) {
      setStep('code');
      setResendTimer(30);
      if (res.masked_phone) setMaskedPhone(res.masked_phone);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
      toast({
        title: 'Code sent',
        description: method === 'sms'
          ? `Check your phone (${res.masked_phone || phone}) for your 4-digit code.`
          : `Check ${email} for your 4-digit code.`,
      });
    } else {
      toast({ title: 'Failed to send code', description: res.error });
    }
  };

  const handleCodeChange = (idx, val) => {
    if (val && !/^\d$/.test(val)) return;
    const newCode = [...code];
    newCode[idx] = val;
    setCode(newCode);
    if (val && idx < 3) inputsRef.current[idx + 1]?.focus();
    const joined = newCode.join('');
    if (joined.length === 4 && !autoVerifyTried) {
      setAutoVerifyTried(true);
      setTimeout(() => verifyCode(joined), 80);
    }
  };

  const handleKey = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'Enter') verifyCode();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 4);
    if (text.length === 4) {
      setCode(text.split(''));
      inputsRef.current[3]?.focus();
      e.preventDefault();
      if (!autoVerifyTried) {
        setAutoVerifyTried(true);
        setTimeout(() => verifyCode(text), 80);
      }
    }
  };

  const switchMethod = (m) => {
    setMethod(m);
    setStep('email');
    setCode(['', '', '', '']);
    setAutoVerifyTried(false);
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] flex items-center justify-center px-5 py-12 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] conic-bg opacity-40" />
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
          {step === 'email' ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#DEECF9] flex items-center justify-center mb-5">
                {method === 'sms' ? (
                  <Smartphone className="w-7 h-7 text-[#0078D4]" />
                ) : (
                  <Mail className="w-7 h-7 text-[#0078D4]" />
                )}
              </div>
              <h1 className="font-display text-3xl text-slate-900">Sign in</h1>
              <p className="text-sm text-slate-500 mt-1">
                {method === 'sms'
                  ? 'Enter your email and phone — we\u2019ll text a 4-digit code.'
                  : 'Enter your email and we\u2019ll send a 4-digit code.'}
              </p>

              {/* Method tabs — only render when SMS OTP is server-enabled */}
              {smsEnabled && (
                <div className="mt-5 inline-flex p-1 bg-slate-100 rounded-full" data-testid="customer-login-method-tabs">
                  <button
                    type="button"
                    onClick={() => switchMethod('email')}
                    className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${
                      method === 'email' ? 'bg-white text-[#0078D4] shadow' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="customer-login-method-email"
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMethod('sms')}
                    className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${
                      method === 'sms' ? 'bg-white text-[#0078D4] shadow' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="customer-login-method-sms"
                  >
                    Phone
                  </button>
                </div>
              )}

              <form onSubmit={requestCode} className="mt-7 space-y-4">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder="your@email.com"
                  className="h-12 rounded-xl px-4 text-base"
                  autoComplete="email"
                  data-testid="customer-login-email-input"
                />

                {method === 'sms' && (
                  <Input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+968 9XXX XXXX"
                    className="h-12 rounded-xl px-4 text-base"
                    autoComplete="tel"
                    data-testid="customer-login-phone-input"
                  />
                )}

                {showPasskeyButton && (
                  <Button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={passkeyLoading || loading}
                    className="w-full h-12 rounded-full bg-slate-900 hover:bg-black text-white font-semibold btn-lift flex items-center justify-center gap-2"
                    data-testid="customer-login-passkey-btn"
                  >
                    {passkeyLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Fingerprint className="w-5 h-5" />
                    )}
                    Sign in with Face ID / Touch ID
                  </Button>
                )}

                <Button
                  type="submit"
                  disabled={loading || passkeyLoading || !email || (method === 'sms' && !phone)}
                  className={`w-full h-12 rounded-full font-semibold btn-lift ${
                    showPasskeyButton
                      ? 'bg-white border border-slate-300 hover:bg-slate-50 text-slate-900'
                      : 'bg-[#0078D4] hover:bg-[#005A9E] text-white'
                  }`}
                  data-testid="customer-login-send-code-btn"
                >
                  {loading
                    ? 'Sending\u2026'
                    : method === 'sms'
                    ? 'Send SMS code'
                    : showPasskeyButton
                    ? 'Use email code instead'
                    : 'Send sign-in code'}{' '}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
              <p className="text-xs text-neutral-500 mt-6 flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                No password needed. We never share your email or phone.
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => { setStep('email'); setCode(['', '', '', '']); setAutoVerifyTried(false); }}
                className="text-sm text-slate-600 hover:text-[#0078D4] flex items-center gap-1 mb-5"
                data-testid="customer-login-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> {method === 'sms' ? 'Use different details' : 'Use different email'}
              </button>
              <h1 className="font-display text-3xl text-slate-900">Enter the code</h1>
              <p className="text-sm text-slate-500 mt-1">
                {method === 'sms' ? (
                  <>We sent a 4-digit code to <b className="text-slate-900">{maskedPhone || phone}</b></>
                ) : (
                  <>We sent a 4-digit code to <b className="text-slate-900">{email}</b></>
                )}
              </p>
              <div className="mt-7 flex justify-between gap-3" onPaste={handlePaste}>
                {[0, 1, 2, 3].map((i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i]}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleKey(i, e)}
                    className="w-16 h-16 text-center text-2xl font-extrabold text-slate-900 border-2 border-neutral-200 rounded-2xl focus:border-[#0078D4] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 transition"
                    data-testid={`customer-login-otp-${i}`}
                  />
                ))}
              </div>
              <Button
                onClick={() => verifyCode()}
                disabled={loading || code.join('').length !== 4}
                className="w-full h-12 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift mt-6"
                data-testid="customer-login-verify-btn"
              >
                {loading ? 'Verifying\u2026' : 'Verify and continue'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <div className="mt-5 text-center">
                {resendTimer > 0 ? (
                  <p className="text-xs text-neutral-500">Resend code in {resendTimer}s</p>
                ) : (
                  <button
                    onClick={requestCode}
                    className="text-xs text-[#0078D4] font-semibold hover:underline"
                    data-testid="customer-login-resend-btn"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default CustomerLogin;
